/**
 * Quote resume access token — client-side storage helpers.
 *
 * A signed, single-quote-scoped 30-day token carried on the patient's emailed/
 * SMS resume link as `?qt=<token>`. It grants FULL read access to that one
 * quote so the funnel resume (financing / Book Now photo gate / $99-$249 tier)
 * renders, without a portal login. See server/services/quoteAccessTokenService.ts
 * and .claude/plans/twinkling-doodling-quasar.md.
 *
 * SECURITY — keep this token on its OWN lane:
 *  - Storage key is `quoteResumeToken`, NEVER `token`. The patient-portal JWT
 *    lives under `token` and relayEnvironment.ts forwards it as
 *    `Authorization: Bearer` on every request; reusing that key would let the
 *    two credentials clobber each other and round-trip the quote token into the
 *    portal-ownership path (a scope-escape).
 *  - It is transported as the dedicated `X-Quote-Token` header, never Bearer.
 *  - The server still verifies `grant.quoteId === args.id`, so a stale token
 *    for quote A presented while loading quote B is rejected (B stays anonymous).
 */
export const QUOTE_RESUME_TOKEN_KEY = "quoteResumeToken";

interface StoredQuoteResumeToken {
  quoteId: string;
  token: string;
}

export function storeQuoteResumeToken(quoteId: string, token: string): void {
  try {
    const payload: StoredQuoteResumeToken = { quoteId, token };
    localStorage.setItem(QUOTE_RESUME_TOKEN_KEY, JSON.stringify(payload));
  } catch {
    // Private-browsing / storage-disabled — the header just won't be sent and
    // the funnel falls back to the anonymous path. Non-fatal.
  }
}

/** The raw token last consumed from a `?qt=` resume link, or null. */
export function readQuoteResumeToken(): string | null {
  try {
    const raw = localStorage.getItem(QUOTE_RESUME_TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredQuoteResumeToken;
    return parsed?.token || null;
  } catch {
    return null;
  }
}
